// The "Request leave" form. As you change anything it works out the days, spots clashes and shows what happens next.

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { checkBooking } from '../lib/booking';
import { fmtRange, formatDays } from '../lib/dates';
import { TYPE_LIST } from '../lib/leave';
import { useStore } from '../store/StoreProvider';
import { useCtx } from '../store/useCtx';
import type { DayPart, EndPart, LeaveTypeId, RequestDraft, StartPart } from '../types';
import { byName, typeStyle } from '../ui/bits';
import type { RequestDialogOptions } from '../ui/dialogs';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toasts';

export function RequestDialog({ date, userId, onClose }: RequestDialogOptions & { onClose: () => void }) {
  const ctx = useCtx();
  const store = useStore();
  const toast = useToast();
  const { me } = ctx;
  const isHR = me.role === 'hr';
  const people = useMemo(() => (isHR ? ctx.activeUsers : [me]).slice().sort(byName), [isHR, ctx.activeUsers, me]);

  const [forId, setForId] = useState(userId || me.id);
  const [typeId, setTypeId] = useState<LeaveTypeId>('holiday');
  const [start, setStart] = useState(date || ctx.today);
  const [end, setEnd] = useState(date || ctx.today);
  const [dayPart, setDayPart] = useState<DayPart>('full');
  const [startPart, setStartPart] = useState<StartPart>('full');
  const [endPart, setEndPart] = useState<EndPart>('full');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const startInput = useRef<HTMLInputElement>(null);

  useEffect(() => startInput.current?.focus(), []);

  const lastDay = end || start;
  const multi = !!start && start !== lastDay;
  const draft: RequestDraft = {
    userId: isHR ? forId : me.id,
    typeId,
    start,
    end: lastDay,
    startPart: multi ? startPart : dayPart === 'pm' ? 'pm' : 'full',
    endPart: multi ? endPart : dayPart === 'am' ? 'am' : 'full',
    note,
  };
  const { days, problems, notes } = checkBooking(draft, ctx);

  const changeStart = (value: string) => {
    setStart(value);
    // Moving the start date past the end date drags the end date along with it.
    if (value && (!end || end < value)) setEnd(value);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (problems.length || sending) return;
    setSending(true);
    try {
      const req = await store.createRequest(draft);
      onClose();
      toast(req.status === 'pending' ? 'Request sent for approval.' : 'Leave recorded.');
    } catch (err) {
      console.error(err);
      setSending(false);
      toast('Could not save that request. Please try again.', 'error');
    }
  };

  return (
    <Modal title="Request leave" titleId="req-title" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {isHR && (
          <div className="field">
            <label htmlFor="f-user">Who is this for?</label>
            <select id="f-user" value={forId} onChange={(e) => setForId(e.target.value)}>
              {people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.id === me.id ? ' (you)' : ''}</option>
              ))}
            </select>
            <p className="hint">Leave you record for someone else is approved straight away.</p>
          </div>
        )}

        <fieldset className="field">
          <legend>Type of leave</legend>
          <div className="type-picker">
            {TYPE_LIST.map((t) => (
              <label key={t.id} className="type-option" style={typeStyle(t)}>
                <input type="radio" name="typeId" value={t.id} checked={typeId === t.id} onChange={() => setTypeId(t.id)} />
                <span className="type-card"><Icon name={t.icon} size={18} /><span>{t.label}</span></span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="f-start">From</label>
            <input id="f-start" ref={startInput} type="date" value={start} onChange={(e) => changeStart(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="f-end">To</label>
            <input id="f-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} required />
          </div>
        </div>

        {multi ? (
          <div className="grid-2">
            <div className="field">
              <label htmlFor="f-startPart">First day</label>
              <select id="f-startPart" value={startPart} onChange={(e) => setStartPart(e.target.value as StartPart)}>
                <option value="full">Full day</option>
                <option value="pm">Afternoon only</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-endPart">Last day</label>
              <select id="f-endPart" value={endPart} onChange={(e) => setEndPart(e.target.value as EndPart)}>
                <option value="full">Full day</option>
                <option value="am">Morning only</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="f-dayPart">Length of day</label>
            <select id="f-dayPart" value={dayPart} onChange={(e) => setDayPart(e.target.value as DayPart)}>
              <option value="full">Full day</option>
              <option value="am">Morning only</option>
              <option value="pm">Afternoon only</option>
            </select>
          </div>
        )}

        <div className="field">
          <label htmlFor="f-note">Note <span className="optional">(optional)</span></label>
          <textarea id="f-note" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="summary" aria-live="polite">
          {!problems.length && (
            <p className="summary-days">
              <strong>{formatDays(days)}</strong> working day{days === 1 ? '' : 's'}{' '}
              <span className="muted">· {fmtRange(draft.start, draft.end)}</span>
            </p>
          )}
          {problems.map((p) => (
            <p key={p} className="summary-line summary-error"><Icon name="alert" size={16} /><span>{p}</span></p>
          ))}
          {notes.map((n) => (
            <p key={n.text} className={`summary-line ${n.warn ? 'summary-warn' : ''}`.trim()}>
              <Icon name={n.warn ? 'alert' : 'info'} size={16} /><span>{n.text}</span>
            </p>
          ))}
        </div>

        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn" disabled={problems.length > 0 || sending}>
            {sending ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
